import { useEffect } from "react";
import { useGridApiRef } from "@mui/x-data-grid-premium";

// Applies an "equals" filter (from a query-param value) on a MaterialUiTable
// column, so another screen can link to the table pre-filtered.
// Routing-agnostic on purpose: the consuming app reads the URL param and passes
// its value. The design system must NOT call react-router hooks — in a build it
// resolves a second react-router instance and crashes outside the app <Router>.
export const useUrlTableFilter = (
  apiRef: ReturnType<typeof useGridApiRef>,
  isReady: boolean,
  config: {
    value: string | null;
    field: string;
    filterId: string;
    // Maps the raw param value to the value stored in the column. Returning
    // undefined removes the filter (e.g. the param has no match in the list).
    // When omitted, the raw value is used as-is.
    resolveValue?: (rawValue: string) => string | undefined;
  },
) => {
  const { value: rawParam, field, filterId, resolveValue } = config;

  useEffect(() => {
    const listIsNotReady =
      !apiRef.current?.upsertFilterItem ||
      !apiRef.current?.deleteFilterItem ||
      !isReady;

    if (listIsNotReady) {
      return;
    }

    const alreadyInFilter = apiRef.current.state.filter.filterModel.items.find(
      (item) => item.id === filterId,
    );

    const value =
      rawParam === null
        ? undefined
        : resolveValue
          ? resolveValue(rawParam)
          : rawParam;

    if (value === undefined) {
      if (alreadyInFilter) {
        apiRef.current.deleteFilterItem(alreadyInFilter);
      }
      return;
    }

    apiRef.current.upsertFilterItem({
      id: filterId,
      field,
      operator: "equals",
      value,
    });
  }, [apiRef, isReady, rawParam, field, filterId, resolveValue]);
};
